import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSingleSelectOption} from '@github-ui/issue-fields-shared/issue-field'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {clsx} from 'clsx'
import {useCallback, useMemo} from 'react'

import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import fieldItemStyles from './IssueFieldItem.module.css'
import {IssueFieldSingleSelectValueToken} from './IssueFieldSingleSelectValueToken'
import styles from './IssueFieldSingleSelectValueToken.module.css'

type IssueFieldCommentEditorProps = {fieldId: string; fieldName: string; readonly: boolean}

export type SingleSelectInitialValue = {
  name: string
  color: string
  description?: string
} | null
export type SingleSelectOnCommit = (fieldId: string, value: IssueFieldSingleSelectOption | null) => void

type IssueFieldSingleSelectEditorBaseProps = {
  fieldId: string
  fieldName: string
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  shouldOpen?: boolean
  isDraft?: boolean
}

export type IssueFieldSingleSelectEditorProps = IssueFieldCommentEditorProps & {
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  /// If set to true, then the single select editor should render in open state
  shouldOpen?: boolean
  isDraft?: boolean
  className?: string
}

export type IssueFieldCompactSingleSelectEditorInternalProps = IssueFieldSingleSelectEditorBaseProps & {
  readonly: boolean
  className?: string
}

export const IssueFieldCompactSingleSelectEditorInternal = ({
  fieldId,
  fieldName,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  readonly,
  className,
}: IssueFieldCompactSingleSelectEditorInternalProps) => {
  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const isNoValue = !initialValue || (initialValue.name === '' && initialValue.color === '')

  const displayValue = isNoValue ? (
    LABELS.setValue
  ) : (
    <IssueFieldSingleSelectValueToken
      name={initialValue.name}
      color={initialValue.color}
      getTooltipText={() => undefined}
      size="small"
      containerClassName={styles.compactContainer}
    />
  )

  const ariaLabel = isNoValue ? `Set ${fieldName}` : `Edit ${fieldName}`

  if (readonly) {
    return (
      <div className={clsx(fieldItemStyles.issueFieldFormWrapper, className)}>
        <p className={fieldItemStyles.issueFieldLabel}>{fieldName}</p>
        <div
          className={clsx(
            fieldItemStyles.issueFieldValueText,
            !isNoValue && fieldItemStyles.hasValue,
            styles.compactValueContainer,
          )}
        >
          {displayValue}
        </div>
      </div>
    )
  }

  return (
    <IssueSingleSelectFieldPicker
      fieldId={fieldId}
      fieldName={fieldName}
      selectedOption={initialValue ? initialValue.name : null}
      onSelectionChange={onSelectionChanged}
      anchorElement={(anchorProps, ref) => (
        <button
          ref={ref}
          className={clsx(
            fieldItemStyles.issueFieldFormWrapper,
            fieldItemStyles.issueFieldFormWrapperClickable,
            className,
          )}
          aria-label={ariaLabel}
          {...anchorProps}
        >
          <span className={fieldItemStyles.issueFieldLabel}>{fieldName}</span>
          <span
            className={clsx(
              fieldItemStyles.issueFieldValueText,
              !isNoValue && fieldItemStyles.hasValue,
              styles.compactValueContainer,
            )}
          >
            {displayValue}
          </span>
        </button>
      )}
      readonly={false}
      isLazy
      onClose={onClose}
      shouldOpen={shouldOpen}
    />
  )
}

export const IssueFieldSingleSelectEditor = ({
  fieldId,
  fieldName,
  readonly,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  className,
}: IssueFieldSingleSelectEditorProps) => {
  const isCompactViewEnabled = isFeatureEnabled('issue_fields_compact_view')

  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const sectionHeader = useMemo(() => {
    return (
      <IssueSingleSelectFieldPicker
        fieldId={fieldId}
        fieldName={fieldName}
        selectedOption={initialValue ? initialValue.name : null}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={fieldName || ''} buttonProps={anchorProps} ref={ref} readonly={readonly} />
        )}
        readonly={false}
        isLazy
        onClose={onClose}
        shouldOpen={shouldOpen}
      />
    )
  }, [fieldId, fieldName, initialValue, onClose, onSelectionChanged, readonly, shouldOpen])

  if (isCompactViewEnabled) {
    return (
      <IssueFieldCompactSingleSelectEditorInternal
        fieldId={fieldId}
        fieldName={fieldName}
        initialValue={initialValue}
        onCommit={onCommit}
        shouldOpen={shouldOpen}
        isDraft={isDraft}
        readonly={readonly}
        className={className}
      />
    )
  }

  const isNoValue = initialValue?.name === '' && initialValue?.color === '' && initialValue?.description === ''
  return (
    <Section sectionHeader={sectionHeader} className={className}>
      <div className={styles.fieldEditorContainer}>
        {isNoValue ? (
          <div className={styles.noValueContainer}>{LABELS.setValue}</div>
        ) : (
          <IssueFieldSingleSelectValueToken
            name={initialValue?.name || ''}
            color={initialValue?.color || ''}
            getTooltipText={isTextTruncated => (isTextTruncated ? (initialValue?.description ?? '') : undefined)}
          />
        )}
      </div>
    </Section>
  )
}
